import { motion, AnimatePresence } from 'framer-motion';
import React, { useCallback, useEffect, useState } from 'react';
import { API } from '../../shared/api/Api';
import axios from 'axios';
import { showToast } from '../../utils/utils';
import { useSelector } from 'react-redux';
import { selectUserInformations } from '../../features/authSlice';
import Close from '../../assets/svg/CloseButton.svg';
import Loader from '../Live/Loader';

// Animation variants
const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.3, ease: "easeInOut" }
    }
};

const itemVariants = {
    hidden: { opacity: 0, y: -10 },
    visible: (i) => ({
        opacity: 1,
        y: 0,
        transition: { delay: i * 0.05 }
    })
};

const RemoveRoleFromUser = ({ role, onClose }) => {
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [loading, setLoading] = useState(false);
    const [removing, setRemoving] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    const user = useSelector(selectUserInformations);

    const fetchUsersByRole = useCallback(async () => {
        try {
            if (role?.uuid) {
                const response = await axios.get(
                    `${API.Roles.app_roles_getUsersByRole}/${role?.uuid}`,
                    { headers: { Authorization: `Bearer ${user.token}` } }
                );
                const data = response.data;

                if (data.status === 200 && data.errorCode === 0) {
                    setUsers(data.data.items);
                } else {
                    showToast("Error", "Failed to fetch users", "error");
                }
            }
        } catch (error) {
            showToast(
                "Error",
                error.response?.data?.message || "An error occurred",
                "error"
            );
        }
    }, [user?.token, role?.uuid]);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            await fetchUsersByRole();
            setLoading(false);
        };
        fetchData();
    }, [fetchUsersByRole]);

    const handleRemoveRole = async () => {
        if (!selectedUser) {
            showToast('Error', 'Please select a user', 'error');
            return;
        }

        setRemoving(true);
        try {
            const response = await axios.post(
                `${API.Roles.app_roles_removeRoleFromUser}/${selectedUser}`,
                { role_uuid: role?.uuid },
                { headers: { Authorization: `Bearer ${user.token}` } }
            );

            if (response.data.status === 200) {
                showToast("Success", "Role removed successfully", "success");
                await fetchUsersByRole(); // Refresh the list
                setSelectedUser(null);
            }
        } catch (error) {
            showToast(
                "Error",
                error.response?.data?.message || "Failed to remove role",
                "error"
            );
        } finally {
            setRemoving(false);
        }
    };

    const filteredUsers = users.filter(user =>
        user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.email.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <motion.div
            className="p-6 space-y-6"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
        >
            {/* Header */}
            <div className="flex justify-between items-center pb-4 border-b border-gray-200">
                <div>
                    <h3 className="text-2xl font-bold text-gray-900">
                        Remove Role: {role?.name}
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">UUID: {role?.uuid}</p>
                </div>
                <button
                    onClick={onClose}
                    className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                    <img className="w-6 h-6" src={Close} alt="Close" />
                </button>
            </div>

            {/* User Search and Selection */}
            <div className="space-y-4">
                <div className="relative">
                    <input
                        type="text"
                        placeholder="Search users..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full px-4 py-2 rounded-md border border-gray-300 focus:ring-2 focus:ring-blue-500"
                    />
                    <span className="absolute right-3 top-2.5 text-gray-400">
                        {filteredUsers.length} users
                    </span>
                </div>

                {loading ? (
                    <div className="flex justify-center py-8">
                        <Loader />
                    </div>
                ) : (
                    <motion.div
                        className="border rounded-lg overflow-hidden"
                        layout
                    >
                        <AnimatePresence>
                            {filteredUsers.map((user, index) => (
                                <motion.div
                                    key={user.uuid}
                                    className={`p-4 border-b last:border-b-0 cursor-pointer ${selectedUser === user.uuid
                                        ? 'bg-blue-50'
                                        : 'bg-white hover:bg-gray-50'
                                        }`}
                                    variants={itemVariants}
                                    custom={index}
                                    initial="hidden"
                                    animate="visible"
                                    exit="hidden"
                                    whileHover={{ scale: 1.02 }}
                                    onClick={() => setSelectedUser(user.uuid)}
                                >
                                    <div className="flex items-center">
                                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mr-4 ${selectedUser === user.uuid
                                            ? 'border-blue-500 bg-blue-500'
                                            : 'border-gray-300'
                                            }`}>
                                            {selectedUser === user.uuid && (
                                                <svg className="w-3 h-3 text-white" viewBox="0 0 20 20" fill="currentColor">
                                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                </svg>
                                            )}
                                        </div>
                                        <div>
                                            <p className="font-medium text-gray-900">{user.name}</p>
                                            <p className="text-sm text-gray-500">{user.email}</p>
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </AnimatePresence>

                        {filteredUsers.length === 0 && (
                            <div className="p-6 text-center text-gray-500">
                                No users found matching your search
                            </div>
                        )}
                    </motion.div>
                )}
            </div>

            {/* Footer */}
            <div className="flex justify-end space-x-4 border-t pt-4">
                <motion.button
                    onClick={onClose}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                >
                    Cancel
                </motion.button>
                <motion.button
                    onClick={handleRemoveRole}
                    disabled={removing || !selectedUser}
                    className="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                    whileHover={{ scale: removing ? 1 : 1.05 }}
                    whileTap={{ scale: removing ? 1 : 0.95 }}
                >
                    {removing ? <Loader /> : 'Remove Role'}
                </motion.button>
            </div>
        </motion.div>
    );
};

export default RemoveRoleFromUser;